import React, { useEffect, useState } from 'react';
import Drawer from '../components/Drawer';
import Footer from '../components/Footer';
import { getOrganization, updateOrganization } from '../api/api';
import { useAuth } from '../context/AuthContext';

export default function OrganizationSettingsPage() {
  const { activeOrg, canManageOrganizationSettings } = useAuth();
  const [form, setForm] = useState({
    name: '',
    industry: '',
    email: '',
    phone: '',
    country: '',
    currency: 'USD',
    timezone: 'UTC',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!activeOrg?.id) return;
      setLoading(true);
      setError('');
      try {
        const res = await getOrganization(activeOrg.id);
        const org = res?.data?.data?.organization || {};
        setForm({
          name: org.name || '',
          industry: org.industry || '',
          email: org.email || '',
          phone: org.phone || '',
          country: org.country || '',
          currency: org.currency || 'USD',
          timezone: org.timezone || 'UTC',
        });
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load organization settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeOrg?.id]);

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canManageOrganizationSettings) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await updateOrganization(activeOrg.id, form);
      setMessage('Organization settings updated successfully.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update organization settings.');
    } finally {
      setSaving(false);
    }
  };

  const pageContent = (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 space-y-6">
      <section className="rounded-3xl border p-6 shadow-sm" style={{ borderColor: '#f4d6df', background: '#fff' }}>
        <h1 className="text-3xl font-bold text-slate-900">Organization Settings</h1>
        <p className="text-sm text-slate-500 mt-2">Update your tenant profile, regional defaults, and operational details.</p>
      </section>

      {message ? <Banner tone="success" text={message} /> : null}
      {error ? <Banner tone="error" text={error} /> : null}

      <form onSubmit={onSubmit} className="rounded-3xl border p-6 shadow-sm space-y-4" style={{ borderColor: '#f4d6df', background: '#fff' }}>
        {loading ? <p className="text-sm text-slate-500">Loading organization settings...</p> : null}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Organization Name" name="name" value={form.name} onChange={onChange} disabled={!canManageOrganizationSettings} />
          <Field label="Industry" name="industry" value={form.industry} onChange={onChange} disabled={!canManageOrganizationSettings} />
          <Field label="Support Email" name="email" type="email" value={form.email} onChange={onChange} disabled={!canManageOrganizationSettings} />
          <Field label="Phone" name="phone" value={form.phone} onChange={onChange} disabled={!canManageOrganizationSettings} />
          <Field label="Country" name="country" value={form.country} onChange={onChange} disabled={!canManageOrganizationSettings} />
          <Field label="Currency" name="currency" value={form.currency} onChange={onChange} disabled={!canManageOrganizationSettings} />
          <div className="md:col-span-2">
            <Field label="Timezone" name="timezone" value={form.timezone} onChange={onChange} disabled={!canManageOrganizationSettings} />
          </div>
        </div>
        <button disabled={!canManageOrganizationSettings || saving} className="rounded-xl px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: '#c44569' }}>
          {saving ? 'Saving Changes...' : 'Save Organization Settings'}
        </button>
        {!canManageOrganizationSettings ? <p className="text-xs text-slate-400">You have read-only access to organization settings.</p> : null}
      </form>
    </div>
  );

  return <><Drawer pageHeader={pageContent} /><Footer /></>;
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
      <input {...props} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" />
    </label>
  );
}

function Banner({ tone, text }) {
  const styles = tone === 'success'
    ? { borderColor: '#a7f3d0', background: '#ecfdf5', color: '#047857' }
    : { borderColor: '#f9b8ca', background: '#fce0e8', color: '#c44569' };
  return <div className="rounded-2xl border px-4 py-3 text-sm" style={styles}>{text}</div>;
}
